import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth as useClerkAuth, useClerk } from '@clerk/react';
// The classic headless sign-in/sign-up hooks live under the /legacy entrypoint.
// (@clerk/react's top-level hooks are the newer signals-based API with a different shape.)
import { useSignIn, useSignUp } from '@clerk/react/legacy';
import Footer from '../components/Footer';
import './LoginPage.css';

const CODE_LENGTH = 6;
const RESEND_SECONDS = 30;

const LoginPage = () => {
    const navigate = useNavigate();
    const { isSignedIn } = useClerkAuth();
    const clerk = useClerk();
    const { isLoaded: siLoaded, signIn } = useSignIn();
    const { isLoaded: suLoaded, signUp } = useSignUp();

    const [mode, setMode] = useState('signin');       // 'signin' | 'signup'
    const [view, setView] = useState('main');          // 'main' | 'code'
    const [codeContext, setCodeContext] = useState('signin'); // which flow the code belongs to
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [resendIn, setResendIn] = useState(0);
    const codeRefs = useRef([]);

    const ready = siLoaded && suLoaded;

    // Already authenticated -> go straight to the app.
    // (Dashboard's gate routes users without an active plan to /verified.)
    useEffect(() => {
        if (isSignedIn) navigate('/app');
    }, [isSignedIn, navigate]);

    // Resend cooldown ticker.
    useEffect(() => {
        if (resendIn <= 0) return undefined;
        const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendIn]);

    const clerkError = (err) =>
        err?.errors?.[0]?.longMessage ||
        err?.errors?.[0]?.message ||
        err?.message ||
        'Something went wrong. Please try again.';

    const switchMode = (m) => {
        setMode(m);
        setError('');
    };

    const goCode = (context) => {
        setCodeContext(context);
        setCode(Array(CODE_LENGTH).fill(''));
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setView('code');
        setResendIn(RESEND_SECONDS);
        setTimeout(() => codeRefs.current[0]?.focus(), 50);
    };

    const finish = async (sessionId) => {
        await clerk.setActive({ session: sessionId });
        navigate('/app');
    };

    // --- Password sign-in OR start of sign-up (email + password) ---
    const submitMain = async (e) => {
        e.preventDefault();
        if (!ready || busy) return;
        setError('');
        setBusy(true);
        try {
            if (mode === 'signin') {
                const res = await signIn.create({ identifier: email.trim(), password });
                if (res.status === 'complete') {
                    await finish(res.createdSessionId);
                    return;
                }
                setError('Extra verification is needed. Try “Email me a code instead”.');
            } else {
                const [firstName, ...rest] = fullName.trim().split(' ');
                await signUp.create({
                    emailAddress: email.trim(),
                    password,
                    ...(firstName ? { firstName, lastName: rest.join(' ') || undefined } : {}),
                });
                await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
                goCode('signup');
            }
        } catch (err) {
            setError(clerkError(err));
        } finally {
            setBusy(false);
        }
    };

    // --- Passwordless: email me a sign-in code ---
    const startEmailCode = async () => {
        if (!ready || busy) return;
        if (!email.trim()) {
            setError('Enter your email first, then request a code.');
            return;
        }
        setError('');
        setBusy(true);
        try {
            const res = await signIn.create({ identifier: email.trim() });
            const factor = res.supportedFirstFactors?.find((f) => f.strategy === 'email_code');
            if (!factor) throw new Error('Email codes aren’t available for this account.');
            await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: factor.emailAddressId });
            goCode('signin');
        } catch (err) {
            setError(clerkError(err));
        } finally {
            setBusy(false);
        }
    };

    // --- Forgot password: email a reset code ---
    const startForgotPassword = async () => {
        if (!ready || busy) return;
        if (!email.trim()) {
            setError('Enter your email first, then request a reset.');
            return;
        }
        setError('');
        setBusy(true);
        try {
            await signIn.create({ strategy: 'reset_password_email_code', identifier: email.trim() });
            goCode('reset');
        } catch (err) {
            setError(clerkError(err));
        } finally {
            setBusy(false);
        }
    };

    // --- Verify the 6-digit code (sign-in / sign-up / password reset) ---
    const verifyCode = async (e) => {
        if (e) e.preventDefault();
        if (!ready || busy) return;
        const value = code.join('');
        if (value.length < CODE_LENGTH) {
            setError('Enter the full 6-digit code.');
            return;
        }
        if (codeContext === 'reset') {
            if (newPassword.length < 8) {
                setError('New password must be at least 8 characters.');
                return;
            }
            if (newPassword !== confirmPassword) {
                setError('Passwords don’t match.');
                return;
            }
        }
        setError('');
        setBusy(true);
        try {
            if (codeContext === 'signup') {
                const res = await signUp.attemptEmailAddressVerification({ code: value });
                if (res.status === 'complete') {
                    await finish(res.createdSessionId);
                    return;
                }
            } else if (codeContext === 'reset') {
                const res = await signIn.attemptFirstFactor({
                    strategy: 'reset_password_email_code',
                    code: value,
                    password: newPassword,
                });
                if (res.status === 'complete') {
                    await finish(res.createdSessionId);
                    return;
                }
            } else {
                const res = await signIn.attemptFirstFactor({ strategy: 'email_code', code: value });
                if (res.status === 'complete') {
                    await finish(res.createdSessionId);
                    return;
                }
            }
            setError('That code didn’t work. Please try again.');
        } catch (err) {
            setError(clerkError(err));
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        if (resendIn > 0 || busy) return;
        setError('');
        setBusy(true);
        try {
            if (codeContext === 'signup') {
                await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
            } else if (codeContext === 'reset') {
                await signIn.create({ strategy: 'reset_password_email_code', identifier: email.trim() });
            } else {
                const factor = signIn.supportedFirstFactors?.find((f) => f.strategy === 'email_code');
                await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: factor?.emailAddressId });
            }
            setResendIn(RESEND_SECONDS);
        } catch (err) {
            setError(clerkError(err));
        } finally {
            setBusy(false);
        }
    };

    // --- Google OAuth (redirect flow via /sso-callback) ---
    const google = async () => {
        if (!ready || busy) return;
        setError('');
        try {
            await signIn.authenticateWithRedirect({
                strategy: 'oauth_google',
                redirectUrl: '/sso-callback',
                redirectUrlComplete: '/verified',
            });
        } catch (err) {
            setError(clerkError(err));
        }
    };

    // --- Code box helpers ---
    const onCodeChange = (i, v) => {
        const digit = v.replace(/\D/g, '').slice(-1);
        setCode((prev) => {
            const next = [...prev];
            next[i] = digit;
            return next;
        });
        if (digit && i < CODE_LENGTH - 1) codeRefs.current[i + 1]?.focus();
    };

    const onCodeKey = (i, e) => {
        if (e.key === 'Backspace' && !code[i] && i > 0) codeRefs.current[i - 1]?.focus();
    };

    const onCodePaste = (e) => {
        const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
        if (!text) return;
        e.preventDefault();
        const next = Array(CODE_LENGTH).fill('');
        text.split('').forEach((d, idx) => {
            next[idx] = d;
        });
        setCode(next);
        codeRefs.current[Math.min(text.length, CODE_LENGTH - 1)]?.focus();
    };

    const isSignup = mode === 'signup';

    return (
        <div className="vd-auth-page">
            <main className="vd-auth-main">
                {view === 'main' ? (
                    <div className="vd-card">
                        <img className="vd-logo" src="/images/just_logo_bgfree.jpg" alt="VerDict AI shield logo" />
                        <h1 className="vd-brandline">VerDict AI</h1>
                        <p className="vd-label">Prototype Testing</p>

                        <div className="vd-tabs" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={!isSignup}
                                className={!isSignup ? 'vd-on' : ''}
                                onClick={() => switchMode('signin')}
                            >
                                Sign In
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={isSignup}
                                className={isSignup ? 'vd-on' : ''}
                                onClick={() => switchMode('signup')}
                            >
                                Create account
                            </button>
                        </div>

                        <form className="vd-form" onSubmit={submitMain}>
                            {isSignup && (
                                <div className="vd-field">
                                    <label htmlFor="vd-name">Full name</label>
                                    <input
                                        id="vd-name"
                                        type="text"
                                        autoComplete="name"
                                        placeholder="Jane Doe"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                    />
                                </div>
                            )}

                            <div className="vd-field">
                                <label htmlFor="vd-email">Email</label>
                                <input
                                    id="vd-email"
                                    type="email"
                                    autoComplete="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="vd-field">
                                <label htmlFor="vd-password">Password</label>
                                <input
                                    id="vd-password"
                                    type="password"
                                    autoComplete={isSignup ? 'new-password' : 'current-password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>

                            {!isSignup && (
                                <div className="vd-row">
                                    <button type="button" className="vd-linklike" onClick={startEmailCode} disabled={busy}>
                                        Email me a code instead
                                    </button>
                                    <button type="button" className="vd-linklike" onClick={startForgotPassword} disabled={busy}>
                                        Forgot password?
                                    </button>
                                </div>
                            )}

                            {error && <div className="vd-error" role="alert">{error}</div>}

                            {/* Clerk bot-protection mounts here when enabled for sign-up */}
                            {isSignup && <div id="clerk-captcha" />}

                            <button type="submit" className="vd-primary" disabled={!ready || busy}>
                                {busy ? 'Please wait…' : isSignup ? 'Create account' : 'Sign In'}
                            </button>

                            <div className="vd-divider">or</div>

                            <button type="button" className="vd-google" onClick={google} disabled={!ready || busy}>
                                <svg viewBox="0 0 48 48" aria-hidden="true">
                                    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.4 5.4 2.5 13.2l7.9 6.1C12.3 13.2 17.6 9.5 24 9.5z" />
                                    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-3.9 6.8-9.7 6.8-17.4z" />
                                    <path fill="#FBBC05" d="M10.4 28.3c-.5-1.5-.8-3-.8-4.8s.3-3.3.8-4.8l-7.9-6.1C.9 15.8 0 19.8 0 23.5s.9 7.7 2.5 10.9l7.9-6.1z" />
                                    <path fill="#34A853" d="M24 47c6.2 0 11.4-2 15.2-5.5l-7.3-5.7c-2 1.4-4.7 2.3-7.9 2.3-6.4 0-11.7-3.7-13.6-9.8l-7.9 6.1C6.4 42.6 14.6 47 24 47z" />
                                </svg>
                                Continue with Google
                            </button>
                        </form>

                        <div className="vd-notice">
                            <svg className="vd-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                <path d="M9 12l2 2 4-4" />
                            </svg>
                            <p>
                                <strong>A note on your data.</strong> We collect only your name and email — to
                                understand app usage and improve the experience.
                            </p>
                        </div>
                        <p className="vd-foot">
                            This is a prototype — results may vary in accuracy as we refine it.
                        </p>
                    </div>
                ) : (
                    <div className="vd-card">
                        <div className="vd-backrow">
                            <button type="button" className="vd-linklike vd-back" onClick={() => setView('main')}>
                                ← Back
                            </button>
                        </div>
                        <img className="vd-logo" src="/images/just_logo_bgfree.jpg" alt="VerDict AI shield logo" style={{ marginTop: 8 }} />
                        <h2 className="vd-code-head">
                            {codeContext === 'signup' ? 'Verify your email'
                                : codeContext === 'reset' ? 'Reset your password'
                                    : 'Enter your sign-in code'}
                        </h2>
                        <p className="vd-code-sub">
                            We sent a 6-digit code to <b>{email.trim() || 'your email'}</b>.{' '}
                            {codeContext === 'reset' ? 'Enter it and choose a new password.' : 'It expires in 10 minutes.'}
                        </p>

                        <form className="vd-form" onSubmit={verifyCode}>
                            <div className="vd-code-boxes" onPaste={onCodePaste}>
                                {code.map((digit, i) => (
                                    <input
                                        key={i}
                                        ref={(el) => { codeRefs.current[i] = el; }}
                                        className="vd-code-input"
                                        inputMode="numeric"
                                        maxLength={1}
                                        aria-label={`Digit ${i + 1}`}
                                        value={digit}
                                        onChange={(e) => onCodeChange(i, e.target.value)}
                                        onKeyDown={(e) => onCodeKey(i, e)}
                                    />
                                ))}
                            </div>

                            {codeContext === 'reset' && (
                                <>
                                    <div className="vd-field">
                                        <label htmlFor="vd-newpass">New password</label>
                                        <input
                                            id="vd-newpass"
                                            type="password"
                                            autoComplete="new-password"
                                            placeholder="••••••••"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                        <span className="vd-hint">At least 8 characters</span>
                                    </div>
                                    <div className="vd-field">
                                        <label htmlFor="vd-confirmpass">Confirm new password</label>
                                        <input
                                            id="vd-confirmpass"
                                            type="password"
                                            autoComplete="new-password"
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                        {confirmPassword && (
                                            <span className={`vd-hint ${newPassword === confirmPassword ? 'vd-hint-ok' : 'vd-hint-bad'}`}>
                                                {newPassword === confirmPassword ? '✓ Passwords match' : 'Passwords don’t match'}
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}

                            {error && <div className="vd-error" role="alert">{error}</div>}

                            <button type="submit" className="vd-primary" disabled={!ready || busy}>
                                {busy
                                    ? (codeContext === 'reset' ? 'Resetting…' : 'Verifying…')
                                    : (codeContext === 'reset' ? 'Reset password & sign in' : 'Verify & continue')}
                            </button>
                        </form>

                        <p className="vd-resend">
                            Didn’t get it?{' '}
                            <button type="button" className="vd-linklike" onClick={resend} disabled={resendIn > 0 || busy}>
                                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                            </button>
                        </p>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default LoginPage;
